/**
 * PlayerPanel — the right-hand column: the whole player pool, and where you set
 * your own prices.
 *
 * TWO MODES, one component, differing only in filtering and header:
 *
 *   NO SLOT SELECTED — the whole pool, every position. This is the DEFAULT
 *   state, not an empty one. It stopped being "the thing you see before you
 *   pick a cell" when the price sheet landed: setting a price is a reason to
 *   browse the list on its own, and needing to click a roster cell first to
 *   reach it was a dead end pretending to be a prompt.
 *
 *   SLOT SELECTED — filtered to that slot's eligible positions, header naming
 *   the slot and cell, sub-line carrying budget and max bid.
 *
 * The my $ column, search and the position pills work identically in both.
 *
 * 🔴 ONE PLAYER PER CLICK. There is no multi-select here and there should not
 * be. addTarget reads the current bucket out of the `buckets` memo to work out
 * where the activeCell should advance to, so two adds dispatched in the same
 * tick would both see the pre-update row and the second would compute its
 * advance from a stale one. The WRITE is safe either way — the updater
 * re-checks inside setPlans — but the cursor would land on the wrong cell, so
 * the user's third pick would silently overwrite their second.
 *
 * Eligibility comes from the slot, and FLEX_ELIGIBLE / SFLEX_ELIGIBLE are
 * imported from rosterSlotFill rather than re-listed. (Note: filterPlayerList
 * exports its own FLEX_ELIGIBLE too — rosterSlotFill is the one the roster
 * builder and the engine's pool sizing both read, so it is the one to trust.)
 * The pool is UNIQUE_PLAYERS priced from dynamicPrices, which is what makes DEF
 * and K come along for free instead of needing a special case.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { theme } from "../../data/theme";
import { UNIQUE_PLAYERS } from "../../data/players";
import { FLEX_ELIGIBLE, SFLEX_ELIGIBLE, rosterablePositions } from "../../utils/rosterSlotFill";
import { getAdpRank, getEffectivePosRank } from "../../data/adpData";
import { shortName } from "../../utils/playerName";
import { targetKey } from "../../hooks/usePlanState";
import PlayerAvatar from "../PlayerAvatar";
import StatsButton, { StatsHintStrip } from "../StatsButton";
import StarToggle from "../StarToggle";
import NumberStepper from "./NumberStepper";
import useBreakevens, { LOOKAHEAD } from "./useBreakevens";
import { makePlayerPpg } from "./ppgTotals";

/**
 * 🔴 ONE GEOMETRY, TWO CONSUMERS — the row and the sticky column header below
 * it. Every width here was a literal in the row before the header existed, and a
 * header whose columns are re-typed literals drifts the first time one of them
 * moves. Anything that changes a column changes both by construction.
 *
 * The gap is `theme.spacing.sm` between EVERY flex child, which is why a header
 * cell that merges two row cells has to add the gap they had between them —
 * see PRICE_GROUP. Get that wrong and the header sits 6px off its column.
 */
const GAP = theme.spacing.sm;
const COL = {
  // 🔴 A COLUMN, AND IT COSTS THE NAME 28px IN THE WIDE ROW — measured, not
  // assumed. At PANEL_WIDTH_WIDE (540) the inner row is 514px and the fixed
  // columns plus their 11 six-pixel gaps already spend ~396 of it, so the
  // flex:1 name had ~118px and now has ~90. Everything else in the row is
  // either a control or a number that cannot ellipse, and the alternatives —
  // hiding the toggle behind hover, or hanging it off the avatar — trade a
  // discoverable control for pixels. The name ellipses and carries a title;
  // an invisible control does not degrade, it just goes missing.
  star: 20,          // the ★ toggle
  add: 22,           // the + button
  avatar: 24,
  rank: 32,
  strike: 26,        // the struck-through base, shown only once overridden
  priceNum: 28,      // NumberStepper `width` — the number itself
  ppgNum: 30,
  spacer: 4,         // the bare span between number groups
  breakeven: 58,
  stats: 28,         // StatsButton, reserved even where it is not rendered
};
// A stepper is wider than its `width`: the arrows and the reserved × slot sit to
// the RIGHT of the number, so the number's right edge is this far off the
// group's. The header pads by it, which is what puts PRICE over the price and
// not over the ×.
const STEPPER_CHROME = 24;
const PRICE_GROUP = COL.strike + GAP + COL.priceNum + STEPPER_CHROME;
const PPG_GROUP = COL.strike + GAP + COL.ppgNum + STEPPER_CHROME;
// Where the name starts on line 1, and where the numbers start on line 2 of the
// two-line layout — the same x, which is what makes one header row possible.
const NAME_INDENT = COL.star + COL.add + COL.avatar + theme.spacing.md * 3;

/**
 * The sticky column header.
 *
 * 🔴 TWO LAYOUTS, AND THEY ARE NOT THE SAME HEADER. Wide is one line, so every
 * column has somewhere to sit. The two-line form puts the name and the rank chip
 * on line 1 and the numbers on line 2, and one header row cannot align with
 * both — so it aligns with the NUMBERS, and PLAYER sits over the name column
 * (line 2's indent IS where the name starts, so the two agree by accident of
 * geometry, and PLAYER gets exactly that indent minus one gap).
 *
 * 🔴 RANK IS DROPPED IN THE TWO-LINE FORM, deliberately. Its chip is on line 1
 * near the right edge, where the numbers line has PPG — a header cell for it
 * would either label the wrong column or push every number column out of
 * alignment. An absent label beats a wrong one.
 */
function ColumnHeader({ wide }) {
  const cell = {
    fontSize: theme.fontSize.caption,
    fontWeight: theme.fontWeight.bold,
    letterSpacing: "1px",
    color: theme.colors.text.faint,
    fontFamily: theme.fonts.body,
  };
  const numbers = (
    <>
      {/* paddingRight, not a narrower width: border-box keeps the cell exactly
          as wide as the row group it labels, and the padding walks the label
          left onto the number. */}
      <span style={{ ...cell, width: PRICE_GROUP, paddingRight: STEPPER_CHROME, textAlign: "right" }}>PRICE</span>
      <span style={{ width: COL.spacer }} />
      <span style={{ ...cell, width: PPG_GROUP, paddingRight: STEPPER_CHROME, textAlign: "right" }}>PPG</span>
      <span style={{ width: COL.spacer }} />
      {/* 🔴 THE CAVEAT LIVES HERE NOW. It used to cost a permanent line in the
          panel subheader; on the header it sits on the number it qualifies, and
          every breakeven cell repeats it in its own title, so hovering either
          the label or a value says the same thing. */}
      <span
        title="“Up to” assumes everyone else goes at their sheet-or-board price."
        style={{ ...cell, width: COL.breakeven, textAlign: "right", cursor: "help" }}
      >UP TO</span>
    </>
  );
  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 1,
      background: theme.colors.bg.surface,
      borderBottom: `1px solid ${theme.colors.border.subtle}`,
      padding: `${theme.spacing.xs}px ${theme.spacing.lg}px`,
      display: "flex", alignItems: "center", gap: GAP,
    }}>
      {wide ? (
        <>
          <span style={{ width: COL.star }} />
          <span style={{ width: COL.add }} />
          <span style={{ width: COL.avatar }} />
          <span style={{ ...cell, flex: 1, minWidth: 0 }}>PLAYER</span>
          <span style={{ ...cell, width: COL.rank, textAlign: "right" }}>RANK</span>
          {numbers}
          <span style={{ width: COL.stats }} />
        </>
      ) : (
        <>
          {/* minus one GAP: the header spends a gap between this cell and the
              first number cell that line 2 spends on padding instead. */}
          <span style={{ ...cell, width: NAME_INDENT - GAP }}>PLAYER</span>
          {numbers}
        </>
      )}
    </div>
  );
}

/** Which positions may fill this slot. BN takes anyone; a named slot takes itself. */
export const ALL_POSITIONS = ["QB", "RB", "WR", "TE", "DEF", "K"];

/**
 * Which positions may fill this slot, in this league.
 *
 * 🔴 FILTERED THROUGH rosterablePositions, IN BOTH MODES. The optimizer already
 * does this — it is how "BN5 Houston Texans $1 in a no-DEF league" was closed —
 * and the panel did not, so a no-DEF league was offered defenses the solver
 * would then refuse to place. Panel and solver have to agree on what the league
 * can roster or the list is advertising players that cannot be used.
 *
 * The BN path had the same hole; the all-positions mode just made it visible.
 *
 * rosterablePositions' "a missing key means PRESENT, only an explicit 0 hides"
 * semantics are deliberately preserved by passing `positions` straight through:
 * the failure modes are asymmetric, and wrongly hiding a position empties
 * somebody's board while wrongly showing one costs a row in a list.
 *
 * `positions` is optional so the pure slot→positions mapping stays testable on
 * its own; omit it and nothing is filtered.
 */
export function eligiblePositions(slot, positions) {
  const base = !slot || slot.pos === "BN"
    // No slot selected is the whole pool, not an empty one — the same list a
    // bench slot accepts, since a bench slot is the other "anyone" case.
    ? ALL_POSITIONS
    : slot.pos === "FLEX" ? FLEX_ELIGIBLE
    : slot.pos === "SFLEX" ? SFLEX_ELIGIBLE
    : [slot.pos];
  if (!positions) return base;
  const rosterable = rosterablePositions(positions);
  return base.filter((pos) => rosterable.has(pos));
}

const ordinal = (cellIdx) => (cellIdx === 0 ? "primary target" : `backup ${cellIdx}`);

export default function PlayerPanel({
  slot,
  cellIdx,
  dynamicPrices,
  plannedKeys,
  left,
  maxBid,
  onAdd,
  placementFor,
  positions,
  leaguePrices,
  onSetSheetPrice,
  onClearSheetPrices,
  leaguePpg,
  onSetSheetPpg,
  onClearSheetPpg,
  leagueStars,
  onToggleStar,
  onClearStars,
  getPlayerProj,
  getPlayerGames,
  wide = false,
  valueOf,
  budget = 0,
  rosterSlots,
  autoFocusSearch = false,
}) {
  const [posFilter, setPosFilter] = useState("ALL");
  // 🔴 ITS OWN BOOLEAN, NOT A VALUE OF posFilter. STARRED combines with the
  // position pills and with slot eligibility; making it a seventh pill would
  // make "my starred WRs" unaskable, which is the question you actually have.
  const [starOnly, setStarOnly] = useState(false);
  const [search, setSearch] = useState("");
  /**
   * 🔴 FOCUS ON MOUNT, AND ONLY WHERE IT IS ASKED FOR. In the phone sheet the
   * keyboard should be up the moment the list appears: the sheet is opened to
   * find somebody, and a tap on a 412px screen to reach the field is a tap the
   * user should not have to make. The desktop panel is always on screen, so
   * stealing focus there would move the caret out of whatever the user was
   * actually typing in.
   *
   * A ref rather than the autoFocus attribute: the panel remounts on every slot
   * change (see the key in DraftPlan), and autoFocus is a mount-time hint the
   * browser is free to ignore inside a freshly inserted subtree.
   */
  const searchRef = useRef(null);
  useEffect(() => {
    if (!autoFocusSearch) return;
    const el = searchRef.current;
    if (el) el.focus();
  }, [autoFocusSearch]);
  const [confirmClearPrices, setConfirmClearPrices] = useState(false);
  const [confirmClearStars, setConfirmClearStars] = useState(false);
  // "Bijan → RB1" for a moment after placing, so a + with no cell selected does
  // not look like nothing happened.
  const [justPlaced, setJustPlaced] = useState(null);
  // 🔴 Memoised, and it matters beyond tidiness: priceOf depends on `sheet`, and
  // useBreakevens invalidates its cache when priceOf's identity changes. A fresh
  // {} every render would throw away every computed breakeven on every render
  // and recompute forever.
  const sheet = useMemo(() => leaguePrices || {}, [leaguePrices]);
  const ppgSheet = useMemo(() => leaguePpg || {}, [leaguePpg]);
  const stars = useMemo(() => leagueStars || {}, [leagueStars]);
  // One count and one control: leaving prices clearable and points not would be
  // the kind of asymmetry nobody discovers until they want it.
  const mySetCount = Object.keys(sheet).length + Object.keys(ppgSheet).length;
  // 🔴 COUNTED SEPARATELY AND CLEARED SEPARATELY. A star is not one of "my
  // numbers": wiping your prices to re-price the board is not a change of mind
  // about who your guys are. Two kinds of thing, two undos.
  const starCount = Object.keys(stars).length;

  const eligible = useMemo(() => eligiblePositions(slot, positions), [slot, positions]);

  // 🔴 THE ENGINE'S RATE, WITH NO OVERRIDES APPLIED — deliberately not the
  // sheet-aware one. This is the number shown struck through beside your own,
  // and what the stepper's × resets to; folding the override in would make it
  // agree with itself and stop being a reference.
  const enginePpgOf = useMemo(
    () => makePlayerPpg(getPlayerProj, getPlayerGames),
    [getPlayerProj, getPlayerGames]
  );

  // 🔴 Rebuilt whenever the price sheet changes — which is exactly what makes
  // the breakeven cache invalidate on it. See useBreakevens.
  const priceOf = useCallback((p) => {
    const k = `${p.name}-${p.pos}`;
    if (sheet[k] !== undefined) return sheet[k];
    return dynamicPrices ? (dynamicPrices.get(k) ?? 1) : 1;
  }, [sheet, dynamicPrices]);

  // How far down the list breakevens are computed. Grows as you scroll, rather
  // than doing the whole pool: ~340 players is ~19 seconds of blocked thread.
  const [visibleLimit, setVisibleLimit] = useState(30);
  const onScroll = useCallback((e) => {
    const el = e.currentTarget;
    const approxRow = wide ? 46 : 62;
    const firstVisible = Math.floor(el.scrollTop / approxRow);
    const onScreen = Math.ceil(el.clientHeight / approxRow);
    setVisibleLimit((prev) => Math.max(prev, firstVisible + onScreen + LOOKAHEAD));
  }, [wide]);

  const rows = useMemo(() => {
    const allowed = new Set(eligible);
    const s = search.trim().toLowerCase();
    return UNIQUE_PLAYERS
      .filter(p => allowed.has(p.pos))
      .filter(p => posFilter === "ALL" || p.pos === posFilter)
      // 🔴 COMBINES, never replaces — eligibility and the position pill both
      // still apply, so a FLEX row filtered to STARRED shows your starred
      // RB/WR/TEs and not your starred QBs.
      .filter(p => !starOnly || stars[targetKey(p)])
      .filter(p => !s || p.name.toLowerCase().includes(s) || (p.team && p.team.toLowerCase().includes(s)))
      .map(p => ({
        player: p,
        key: targetKey(p),
        board: dynamicPrices ? (dynamicPrices.get(targetKey(p)) ?? 1) : 1,
        posRank: getEffectivePosRank(p.name, p.posRank) || p.pos,
        // His own games, not 17 — see ppgTotals. This is the number the
        // stepper resets to, so a wrong divisor here also mis-seeds the edit.
        enginePpg: enginePpgOf(p),
        adp: getAdpRank(p.name),
      }))
      // (a.adp ?? 999): getAdpRank returns undefined for unranked players, and
      // undefined - undefined is NaN — which makes the comparator inconsistent
      // across the many equal-priced $1 players, where this tiebreak is the only
      // thing deciding order.
      .sort((a, b) => b.board - a.board || (a.adp ?? 999) - (b.adp ?? 999))
      .slice(0, 200);
  }, [eligible, posFilter, starOnly, stars, search, dynamicPrices, enginePpgOf]);

  /**
   * What the STARRED pill would actually show: your stars that pass THIS
   * slot's eligibility and the current position pill.
   *
   * 🔴 Deliberately ignores `search`, and deliberately is not the raw league
   * total. A count that moved as you typed would be noise, and the league
   * total would promise 12 on a QB row that can only show you 2.
   *
   * Skipped entirely at zero stars — the list is ~340 players and this runs on
   * every keystroke-free render, so the common case pays nothing.
   */
  const starredInScope = useMemo(() => {
    if (starCount === 0) return 0;
    const allowed = new Set(eligible);
    return UNIQUE_PLAYERS.reduce((n, p) => (
      allowed.has(p.pos)
      && (posFilter === "ALL" || p.pos === posFilter)
      && stars[targetKey(p)] ? n + 1 : n
    ), 0);
  }, [eligible, posFilter, stars, starCount]);

  // 🔴 The breakeven solve needs the WHOLE rosterable pool, not this slot's
  // eligible subset: it asks what the best roster looks like, and that roster
  // has a QB in it whether or not you are looking at a WR slot.
  const solverPool = useMemo(() => {
    const allowed = rosterablePositions(positions || {});
    return UNIQUE_PLAYERS.filter((p) => allowed.has(p.pos));
  }, [positions]);

  const breakevens = useBreakevens({
    rows,
    rosterSlots,
    pool: solverPool,
    priceOf,
    valueOf,
    budget,
    limit: visibleLimit,
    enabled: !!valueOf && !!rosterSlots && rosterSlots.length > 0,
  });

  return (
    <div style={{
      position: "sticky", top: theme.spacing.xl,
      display: "flex", flexDirection: "column",
      maxHeight: "calc(100vh - 120px)",
      borderRadius: theme.radius.xl,
      background: theme.colors.bg.surface,
      border: `1px solid ${theme.colors.border.subtle}`,
      overflow: "hidden",
    }}>
      <div style={{ padding: theme.spacing.lg, borderBottom: `1px solid ${theme.colors.border.subtle}` }}>
        <div style={{
          fontSize: theme.fontSize.base,
          fontWeight: theme.fontWeight.bold,
          color: theme.colors.text.primary,
        }}>
          {slot ? `Select ${ordinal(cellIdx)} for ${slot.label}` : "All players · set your prices"}
        </div>
        <div style={{ fontSize: theme.fontSize.small, color: theme.colors.text.faint, marginTop: 3 }}>
          {slot
            ? `$${Math.round(left)} left overall · max bid $${Math.round(maxBid)} · ${eligible.join("/")}`
            : `$${Math.round(left)} left overall · your numbers beat the board wherever you set them`}
        </div>
        {/* 🔴 ONE HONEST LINE. Once both the price and the points are yours, the
            optimizer will confirm whatever you already decided. That is the
            intended use — it is a thinking tool, not an oracle — but it should
            be visible on screen that that is what is happening. */}
        <div style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint, marginTop: 4 }}>
          Your numbers override the projection for planning only.
        </div>
        {/* The “up to” caveat is on the UP TO column header, where the number
            it qualifies is — see ColumnHeader. It cost a permanent line here
            and bought a line back for the list. */}
      </div>

      <div style={{ padding: theme.spacing.lg, display: "flex", flexDirection: "column", gap: theme.spacing.md, borderBottom: `1px solid ${theme.colors.border.subtle}` }}>
        {/* 🔴 ONE ROW, TWO INDEPENDENT FILTERS. The position pills are still
            suppressed on a single-position slot (a lone pill is noise), but
            STARRED is not tied to them — a QB slot has one position and can
            still have four of your guys in it. The row renders when EITHER has
            something to offer. */}
        {(eligible.length > 1 || starCount > 0) && (
          <div style={{ display: "flex", gap: theme.spacing.xs, flexWrap: "wrap" }}>
            {eligible.length > 1 && ["ALL", ...eligible].map(pos => {
              const on = posFilter === pos;
              return (
                <button
                  key={pos}
                  type="button"
                  onClick={() => setPosFilter(pos)}
                  style={{
                    padding: "3px 9px",
                    borderRadius: theme.radius.sm,
                    border: `1px solid ${on ? theme.colors.border.brand : theme.colors.border.default}`,
                    background: on ? theme.helpers.brandBg(0.12) : theme.colors.bg.raised,
                    color: on ? theme.colors.brand.orange : theme.colors.text.secondary,
                    fontSize: theme.fontSize.small,
                    fontWeight: theme.fontWeight.bold,
                    cursor: "pointer",
                  }}
                >{pos}</button>
              );
            })}
            {/* 🔴 Offered only once you have starred someone. A STARRED (0)
                pill on a fresh league is a control whose only outcome is an
                empty list — the same "never a button that does nothing" rule
                the clear controls below follow. And the COUNT is the point:
                "STARRED (12)" is the reason you reach for the filter, so it is
                on the pill rather than discovered by clicking it.
                DISABLED, not hidden, when the position pill has narrowed the
                scope to none of them — vanishing mid-interaction reads as a
                bug, and the number explains itself. */}
            {starCount > 0 && (
              <button
                type="button"
                disabled={starredInScope === 0 && !starOnly}
                onClick={() => setStarOnly(v => !v)}
                title={starredInScope === 0
                  ? "None of your starred players fit this filter"
                  : "Show only your starred players — combines with the position filter"}
                style={{
                  padding: "3px 9px",
                  borderRadius: theme.radius.sm,
                  border: `1px solid ${starOnly ? theme.colors.star : theme.colors.border.default}`,
                  background: starOnly ? theme.helpers.starBg() : theme.colors.bg.raised,
                  color: starOnly ? theme.colors.star : theme.colors.text.secondary,
                  fontSize: theme.fontSize.small,
                  fontWeight: theme.fontWeight.bold,
                  cursor: starredInScope === 0 && !starOnly ? "not-allowed" : "pointer",
                  opacity: starredInScope === 0 && !starOnly ? 0.45 : 1,
                }}
              >{`★ STARRED (${starredInScope})`}</button>
            )}
          </div>
        )}
        {/* 🔴 Two-step inline, not confirm() — a native modal blocks the page,
            and that pattern was already rejected here. Offered only when there
            is something to clear, so it is never a button that does nothing. */}
        {mySetCount > 0 && (
          <button
            type="button"
            onClick={() => {
              if (confirmClearPrices) {
                onClearSheetPrices && onClearSheetPrices();
                onClearSheetPpg && onClearSheetPpg();
                setConfirmClearPrices(false);
              }
              else { setConfirmClearPrices(true); setTimeout(() => setConfirmClearPrices(false), 3000); }
            }}
            style={{
              alignSelf: "flex-start",
              padding: "2px 8px",
              borderRadius: theme.radius.sm,
              border: `1px solid ${confirmClearPrices ? theme.colors.status.danger : theme.colors.border.default}`,
              background: theme.colors.bg.raised,
              color: confirmClearPrices ? theme.colors.status.danger : theme.colors.text.faint,
              fontSize: theme.fontSize.caption,
              fontWeight: theme.fontWeight.bold,
              fontFamily: theme.fonts.body,
              cursor: "pointer",
            }}
          >
            {confirmClearPrices
              ? `Clear ${mySetCount} of my numbers?`
              : `${mySetCount} of my numbers set — clear`}
          </button>
        )}
        {/* 🔴 ITS OWN CONTROL, and that is the whole point of the third sheet.
            Clearing your prices must not unstar your guys: one is a set of
            numbers you are re-deriving, the other is a judgement about people.
            Same two-step, same "only when there is something to clear". */}
        {starCount > 0 && (
          <button
            type="button"
            onClick={() => {
              if (confirmClearStars) {
                onClearStars && onClearStars();
                setStarOnly(false);
                setConfirmClearStars(false);
              }
              else { setConfirmClearStars(true); setTimeout(() => setConfirmClearStars(false), 3000); }
            }}
            style={{
              alignSelf: "flex-start",
              padding: "2px 8px",
              borderRadius: theme.radius.sm,
              border: `1px solid ${confirmClearStars ? theme.colors.status.danger : theme.colors.border.default}`,
              background: theme.colors.bg.raised,
              color: confirmClearStars ? theme.colors.status.danger : theme.colors.text.faint,
              fontSize: theme.fontSize.caption,
              fontWeight: theme.fontWeight.bold,
              fontFamily: theme.fonts.body,
              cursor: "pointer",
            }}
          >
            {confirmClearStars
              ? `Unstar all ${starCount}?`
              : `${starCount} starred — clear`}
          </button>
        )}
        <input
          ref={searchRef}
          type="text"
          value={search}
          placeholder="Search name or team"
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
            borderRadius: theme.radius.md,
            border: `1px solid ${theme.colors.border.default}`,
            background: theme.colors.bg.raised,
            color: theme.colors.text.primary,
            fontSize: theme.fontSize.sub,
            fontFamily: theme.fonts.body,
          }}
        />
      </div>

      {/* Global one-time nudge. Draft Plan is now the THIRD surface carrying
          the icon (PlayerList and TierBuilder are the others) and the flag is
          global, so whichever surface a user meets first spends it. */}
      <StatsHintStrip />

      <div onScroll={onScroll} style={{ overflowY: "auto", flex: 1 }}>
        {/* Sticky, so the columns stay identified 180 rows down. Suppressed on
            an empty list — labels over nothing are worse than no labels. */}
        {rows.length > 0 && <ColumnHeader wide={wide} />}
        {/* 🔴 An empty list must always carry its own way out. This panel can
            reach "nothing here" with NO visible control to undo it: on a
            single-position slot the pills are suppressed, so a stale posFilter
            from a previous FLEX or bench row leaves an empty pool and no
            affordance. DraftPlan keying this panel on slotId is the real fix;
            this is the net under it, because a blank list with no explanation
            is what made that bug invisible in the first place. */}
        {rows.length === 0 && (
          <div style={{ padding: theme.spacing.xl, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: theme.spacing.md }}>
            <span style={{ color: theme.colors.text.faint, fontSize: theme.fontSize.sub }}>
              No players match.
            </span>
            {/* 🔴 CLEARS EVERY FILTER, INCLUDING STARRED. The net is only a net
                if it catches the newest way to empty the list; a reset that
                left starOnly on would land the user back on the same blank
                page and look broken.
                🔴 And the label no longer reads `slot.pos` unguarded. This
                branch is reachable in POOL mode — no slot at all — via a search
                that matches nothing, and now via STARRED too, where that read
                threw on null. */}
            <button
              type="button"
              onClick={() => { setPosFilter("ALL"); setStarOnly(false); setSearch(""); }}
              style={{
                padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
                borderRadius: theme.radius.md,
                border: `1px solid ${theme.colors.border.brand}`,
                background: theme.helpers.brandBg(0.12),
                color: theme.colors.brand.orange,
                fontSize: theme.fontSize.sub,
                fontWeight: theme.fontWeight.bold,
                fontFamily: theme.fonts.body,
                cursor: "pointer",
              }}
            >{slot ? `Show all ${slot.pos}` : "Show all players"}</button>
          </div>
        )}
        {rows.map(({ player, key, board, posRank, adp, enginePpg }) => {
          // Already somewhere in this plan. Dimmed, not removed: seeing that
          // you already took him is more useful than wondering where he went.
          const planned = plannedKeys && plannedKeys.has(key);
          const mine = sheet[key];
          const minePpg = ppgSheet[key];
          const starred = !!stars[key];
          // In pool mode the + needs somewhere to put him; in slot mode the
          // active cell IS the destination.
          const destination = slot ? null : (placementFor ? placementFor(player) : null);
          const canPlace = !!slot || !!destination;
          const be = breakevens.get(key);
          const placedAt = justPlaced && justPlaced.key === key ? justPlaced.label : null;
          // The four number columns, placed inline when the panel is wide
          // enough and on a second line when it is not.
          // 🔴 STEPPERS, NOT TEXT FIELDS. Nudging is the real use, and a typed
          // field carried a focus-loss risk on every render for the rare case.
          // The base sits in its own fixed slot, shown only once you have
          // overridden it, so the column stays aligned and an untouched row is
          // not two copies of the same number.
          const numbers = (
            <>
              <span
                title="Mock1's board price"
                style={{
                  width: COL.strike, textAlign: "right",
                  fontFamily: theme.fonts.display,
                  fontSize: theme.fontSize.caption,
                  color: theme.colors.text.faint,
                  textDecoration: "line-through",
                }}>{mine !== undefined ? `$${board}` : ""}</span>
              <NumberStepper
                value={mine}
                base={board}
                step={1}
                decimals={0}
                prefix="$"
                width={COL.priceNum}
                title="Your price for this player, used by the optimizer. Hold to run, shift for $10, × to go back to the board."
                onChange={(v) => onSetSheetPrice && onSetSheetPrice(key, v)}
                onReset={() => onSetSheetPrice && onSetSheetPrice(key, null)}
              />

              <span style={{ width: COL.spacer }} />

              <span
                title="Mock1's projected points per game"
                style={{
                  width: COL.strike, textAlign: "right",
                  fontFamily: theme.fonts.display,
                  fontSize: theme.fontSize.caption,
                  color: theme.colors.text.faint,
                  textDecoration: "line-through",
                }}>{minePpg !== undefined ? enginePpg.toFixed(1) : ""}</span>
              <NumberStepper
                value={minePpg}
                base={enginePpg}
                step={0.1}
                decimals={1}
                width={COL.ppgNum}
                title="Your projected points per game, used by the optimizer and every total here. Hold to run, shift for 1.0, × to go back to the projection."
                onChange={(v) => onSetSheetPpg && onSetSheetPpg(key, v)}
                onReset={() => onSetSheetPpg && onSetSheetPpg(key, null)}
              />

              <span style={{ width: COL.spacer }} />

              {/* Breakeven: the most he is worth to this roster. Muted at or
                  below the board — no reason to chase him past market — and
                  quietly emphasised above it, where he is a target. No red, no
                  icons: this is information, not a warning. "—" until the solve
                  lands, which is what progressive computation looks like. */}
              <span
                title={be === undefined
                  ? "Working out what he is worth…"
                  : (be === 0
                    ? "He does not make the best roster at any price"
                    : `Worth up to $${be} to this roster, if everyone else goes at their expected price`)}
                style={{
                  width: COL.breakeven, textAlign: "right",
                  fontFamily: theme.fonts.display,
                  fontSize: theme.fontSize.caption,
                  fontWeight: be !== undefined && be > board ? theme.fontWeight.bold : theme.fontWeight.regular,
                  color: be !== undefined && be > board
                    ? theme.colors.text.secondary
                    : theme.colors.text.faint,
                  whiteSpace: "nowrap",
                }}>
                {be === undefined ? "—" : (be === 0 ? "not worth it" : `up to $${be}`)}
              </span>
            </>
          );

          return (
            <div
              key={key}
              style={{
                padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
                borderBottom: `1px solid ${theme.colors.border.subtle}`,
                opacity: planned ? 0.32 : 1,
              }}
            >
            {/* 🔴 ONE LINE WHEN THE PANEL IS WIDE, TWO WHEN IT IS NOT, and the
                two-line form is the FALLBACK rather than dead code. A 1366 or
                1512 laptop gets no extra pixels and still cannot fit a name plus
                four number columns beside it. Widening the app added a top end;
                it must not remove the bottom. */}
            <div style={{ display: "flex", alignItems: "center", gap: wide ? theme.spacing.sm : theme.spacing.md }}>
              {/* 🔴 THE ONLY PLACE TO STAR SOMEONE, AND THAT IS A MEASURED
                  DECISION, not an omission — see TargetCell for the numbers. A
                  starred PLACED player is reached through search, or through
                  the STARRED pill above.

                  The outline is drawn on every row rather than on hover: an
                  affordance that only exists under the cursor is one nobody
                  finds, and this is the entry point for the whole feature.
                  The button itself is the shared StarToggle — the target card
                  and the PlayerCard portal render the same one.
                  A planned row dims to 0.32 and the star dims with it, because
                  opacity applies to the whole subtree and a child cannot climb
                  back out of its parent's. That is the right answer anyway —
                  the + beside it is in the same state, and the row is still
                  clickable. */}
              <StarToggle
                starred={starred}
                name={player.name}
                size={COL.star}
                testId="list-star-toggle"
                onToggle={() => onToggleStar && onToggleStar(key)}
              />

              {/* 🔴 With no cell selected the + lands the player in the first
                  open PRIMARY he is eligible for, and is DISABLED when there is
                  none. Falling back to any empty cell would drop him into a
                  bench backup, where he is technically placed and effectively
                  invisible — worse than refusing. */}
              <button
                type="button"
                disabled={!canPlace}
                title={planned
                  ? `${player.name} is already in this plan`
                  : (slot
                    ? `Add ${player.name} to ${slot.label}`
                    : (destination
                      ? `Add ${player.name} as ${destination.label}'s primary`
                      : `No open ${player.pos} spot for ${player.name} — every slot he could start is taken. Pick a cell on the left to place him anyway.`))}
                onClick={() => {
                  if (!canPlace || !onAdd) return;
                  onAdd(player);
                  if (!slot && destination) {
                    setJustPlaced({ key, label: destination.label });
                    setTimeout(() => setJustPlaced(null), 2000);
                  }
                }}
                style={{
                  width: COL.add, height: 22, flexShrink: 0, padding: 0, lineHeight: "20px",
                  borderRadius: theme.radius.sm,
                  border: `1px solid ${canPlace ? theme.colors.border.brand : theme.colors.border.default}`,
                  background: canPlace ? theme.helpers.brandBg(0.12) : "transparent",
                  color: canPlace ? theme.colors.brand.orange : theme.colors.text.faint,
                  fontSize: theme.fontSize.sub,
                  fontWeight: theme.fontWeight.bold,
                  cursor: canPlace ? "pointer" : "not-allowed",
                  opacity: canPlace ? 1 : 0.5,
                }}
              >+</button>

              <PlayerAvatar player={player} size={COL.avatar} />

              <div style={{ flex: 1, minWidth: 0 }}>
                {/* 🔴 SHORT HERE, FULL EVERYWHERE ELSE. The column is narrow
                    enough that a long name ellipsed to the point of being
                    unreadable, and an initial reads better than a truncation.
                    The title carries the full name, the search above still
                    matches on it, and TargetCell and the print sheet are
                    untouched: this is a display change in one column, not a
                    change to what a player is called.

                    🔴 DEF IS EXEMPT. A defense's "name" is a team, so the rule
                    turns "Houston Texans" into "H. Texans" -- worse than the
                    thing it was shortening, and not a name anybody would
                    recognise. Kickers are people and shorten like everyone
                    else. */}
                <div
                  title={player.name}
                  style={{
                    fontSize: theme.fontSize.sub,
                    fontWeight: theme.fontWeight.bold,
                    color: theme.colors.text.primary,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}
                  data-testid="panel-row-name"
                >{player.pos === "DEF" ? player.name : shortName(player.name)}</div>
                <div style={{ fontSize: theme.fontSize.caption, color: theme.colors.text.faint }}>
                  <span style={{ color: theme.colors.pos[player.pos] || theme.colors.text.faint, fontWeight: theme.fontWeight.bold }}>
                    {player.pos}
                  </span>
                  {" · "}{player.team || "FA"}
                  {placedAt && (
                    <span style={{ color: theme.colors.status.success, fontWeight: theme.fontWeight.bold }}>
                      {" · → "}{placedAt}
                    </span>
                  )}
                </div>
              </div>

              <span style={{ width: COL.rank, textAlign: "right", fontSize: theme.fontSize.small, color: theme.colors.text.muted }}>
                {posRank}
              </span>
              {/* Same DEF/K suppression PlayerList applies — but the SPACE is
                  reserved either way. Omitting the child took its width and its
                  gap out of the row, and since the name is flex:1 the name grew
                  and every column right of it slid 34px across on DEF and K
                  rows. Invisible until a header sat above them. */}
              {wide && numbers}
              {player.pos !== "DEF" && player.pos !== "K" ? (
                <StatsButton name={player.name} source="draft_plan" />
              ) : (
                <span style={{ width: COL.stats, flexShrink: 0 }} />
              )}
            </div>

            {!wide && (
            <div style={{
              display: "flex", alignItems: "center", gap: GAP,
              paddingLeft: NAME_INDENT, marginTop: 2,
            }}>
              {numbers}
            </div>
            )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
